import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp, Boxes, Package, Truck, CheckCircle2, XCircle,
  Warehouse, Users, MapPin, Activity, AlertTriangle, Trophy,
  Store, FileSpreadsheet, ChevronRight, BarChart3, KeyRound
} from "lucide-react";
import api from "@/api";
import { useLang } from "@/context/LanguageContext";

// Tier 2 (Feb 2026): Super Admin Command Center home tab.
// 16 master cards, each clickable → drilldown OR existing route.
// Cards are big, premium, spaced. Cards display live numbers when available.
const Card = ({ icon: Icon, title, sub, value, accent, onClick, testid, danger }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`group bg-white rounded-2xl p-5 text-left soft-shadow hover:shadow-xl active:scale-[0.99] transition border ${danger ? "border-red-100 hover:border-red-300" : "border-gray-100 hover:border-[#0055FF]/40"} flex flex-col min-h-[140px]`}
  >
    <div className="flex items-start gap-3">
      <div className={`w-11 h-11 rounded-2xl ${accent} grid place-items-center shrink-0`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wider text-[#86868B] font-bold truncate">{sub}</div>
        <div className="heading-font font-bold text-base text-[#1D1D1F] truncate">{title}</div>
      </div>
      <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-[#0055FF] transition shrink-0 mt-1" />
    </div>
    {value !== undefined && value !== null && (
      <div className="mt-auto pt-3">
        <div className="heading-font text-3xl font-bold text-[#1D1D1F]">{value}</div>
      </div>
    )}
  </button>
);

export default function CommandCenter({ onJumpTab }) {
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [overview, setOverview] = useState(null);
  const [depotSum, setDepotSum] = useState(null);
  const [liveCounts, setLiveCounts] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/super-admin").then(r => r.data).catch(() => null),
      api.get("/depot/stock/summary").then(r => r.data).catch(() => null),
      api.get("/tracking/live").then(r => r.data).catch(() => null),
    ]).then(([ov, ds, lt]) => {
      setOverview(ov);
      setDepotSum(ds);
      setLiveCounts(lt?.counts);
    });
  }, []);

  const today = overview?.today || {};

  const sections = [
    [lang === "ta" ? "செயல்திறன்" : "Performance", [
      { id: "today-sales", title: lang === "ta" ? "இன்றைய விற்பனை" : "Today's Sales", sub: lang === "ta" ? "இன்று விற்ற பெட்டிகள்" : "Boxes sold today", icon: Boxes, accent: "bg-[#0055FF]", value: today.units_sold || 0,
        onClick: () => nav("/admin/sales?sort=boxes") },
      { id: "orders", title: t("orders"), sub: lang === "ta" ? "மொத்த ஆர்டர்கள்" : "Total orders today", icon: Package, accent: "bg-[#5856D6]", value: today.total_orders || 0,
        onClick: () => nav("/admin/sales") },
      { id: "delivered", title: lang === "ta" ? "டெலிவரி" : "Delivered", sub: lang === "ta" ? "முடிந்த ஆர்டர்கள்" : "Completed orders", icon: CheckCircle2, accent: "bg-[#34C759]", value: today.delivered || 0,
        onClick: () => nav("/admin/sales?status=delivered") },
      { id: "pending", title: t("pending"), sub: lang === "ta" ? "காத்திருக்கும் ஆர்டர்கள்" : "Awaiting orders", icon: Truck, accent: "bg-[#FF9500]", value: today.pending || 0,
        onClick: () => nav("/admin/sales?status=pending") },
      { id: "not-bought", title: t("not_bought"), sub: lang === "ta" ? "விற்பனை இல்லை" : "Visits without sale", icon: XCircle, accent: "bg-[#FF3B30]", value: today.no_sales || 0,
        onClick: () => nav("/admin/sales?view=nosale") },
    ]],
    [lang === "ta" ? "ஸ்டாக்" : "Stock", [
      { id: "depot-stock", title: t("depot_balance"), sub: lang === "ta" ? "மாவட்டம் வாரியாக" : "By district", icon: Warehouse, accent: "bg-[#0A1A3F]", value: depotSum?.depot_total ?? "—",
        onClick: () => nav("/admin/stock?tab=depot") },
      { id: "on-road-stock", title: lang === "ta" ? "வழியில் உள்ள ஸ்டாக்" : "On-Road Stock", sub: lang === "ta" ? "நபர் வாரியாக" : "By person", icon: Truck, accent: "bg-[#FF9500]", value: depotSum?.on_road_total ?? "—",
        onClick: () => nav("/admin/stock?tab=onroad") },
      { id: "depot-received", title: t("depot_received"), sub: lang === "ta" ? "மாவட்டம் வாரியாக" : "By district", icon: Package, accent: "bg-[#5856D6]", value: undefined,
        onClick: () => nav("/admin/stock?tab=received") },
      { id: "stock-mismatch", title: lang === "ta" ? "ஸ்டாக் வேறுபாடு" : "Stock Mismatch", sub: lang === "ta" ? "நபர் வாரியாக" : "By person", icon: AlertTriangle, accent: "bg-[#FF3B30]", value: undefined,
        onClick: () => nav("/admin/stock?tab=mismatch"), danger: true },
    ]],
    [lang === "ta" ? "களம்" : "Field", [
      { id: "attendance", title: t("attendance") || "Attendance", sub: lang === "ta" ? "இன்று உள்ளவர்கள்" : "Today's check-ins", icon: Activity, accent: "bg-[#34C759]", value: liveCounts?.total ?? "—",
        onClick: () => nav("/management/attendance") },
      { id: "live-tracking", title: t("live_tracking") || "Live Tracking", sub: lang === "ta" ? "நேரலை இடம்" : "Real-time location", icon: MapPin, accent: "bg-[#0055FF]", value: liveCounts?.active ?? "—",
        onClick: () => nav("/management/live-tracking") },
      { id: "location-mismatch", title: lang === "ta" ? "இடம் பொருத்தமின்மை" : "Location Mismatch", sub: lang === "ta" ? "சந்தேகமான வருகை" : "Suspicious visits", icon: AlertTriangle, accent: "bg-[#FF3B30]", value: liveCounts?.suspicious ?? "—",
        onClick: () => nav("/management/live-tracking"), danger: true },
      { id: "top-performers", title: lang === "ta" ? "சிறந்த விற்பனையாளர்கள்" : "Top Performers", sub: lang === "ta" ? "இன்று #1, #2, #3" : "Best of day", icon: Trophy, accent: "bg-[#FF9500]",
        onClick: () => nav("/management/performance") },
    ]],
    [lang === "ta" ? "பயனர்கள் & அறிக்கைகள்" : "Users & Reports", [
      { id: "user-mgmt", title: lang === "ta" ? "ஊழியர் தகவல்" : "Employee Info", sub: lang === "ta" ? "சேல்ஸ்மென் + மேனேஜர்" : "Salesmen + managers", icon: Users, accent: "bg-[#0055FF]",
        onClick: () => nav("/admin/info?tab=employee") },
      { id: "outlets", title: lang === "ta" ? "கடை தகவல்" : "Shop Info", sub: lang === "ta" ? "சில்லறை + மொத்தம்" : "Retailer / Wholesaler", icon: Store, accent: "bg-[#34C759]",
        onClick: () => nav("/admin/info?tab=shop") },
      { id: "products-info", title: lang === "ta" ? "பொருள் தகவல்" : "Product Info", sub: lang === "ta" ? "பொருள் கட்டுப்பாடு" : "Manage products", icon: Package, accent: "bg-[#5856D6]",
        onClick: () => nav("/admin/info?tab=product") },
      { id: "pin-control", title: lang === "ta" ? "PIN கட்டுப்பாடு" : "User PIN Control", sub: lang === "ta" ? "PIN + தடு" : "PIN + block control", icon: KeyRound, accent: "bg-[#FF3B30]",
        onClick: () => nav("/admin/pin-control") },
      { id: "attendance-report", title: t("attendance_report"), sub: lang === "ta" ? "Excel + அச்சு" : "Excel + Print", icon: FileSpreadsheet, accent: "bg-[#34C759]",
        onClick: () => nav("/management/attendance-report") },
      { id: "all-reports", title: lang === "ta" ? "அனைத்து அறிக்கைகள்" : "All Reports", sub: lang === "ta" ? "ஆர்டர்கள் + ஸ்டாக்" : "Orders + stock", icon: BarChart3, accent: "bg-[#0A1A3F]",
        onClick: () => onJumpTab && onJumpTab({ topGroup: "orders", tab: "reports" }) },
    ]],
  ];

  return (
    <div className="space-y-7" data-testid="admin-command-center">
      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0A1A3F] via-[#0055FF] to-[#5856D6] text-white rounded-3xl p-6 sm:p-7 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white/15 grid place-items-center shrink-0">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider opacity-80 font-bold">
              {lang === "ta" ? "சூப்பர் அட்மின்" : "Super Admin"}
            </div>
            <h1 className="heading-font text-2xl sm:text-3xl font-bold">
              {lang === "ta" ? "மாஸ்டர் கட்டுப்பாட்டு மையம்" : "Master Control Center"}
            </h1>
            <p className="text-sm opacity-90 mt-1.5">
              {lang === "ta"
                ? "முக்கிய எண்கள் + அறிக்கைகள் + நேரலை நிலை — ஒரே இடத்தில்"
                : "Live KPIs + reports + system health — in one place."}
            </p>
          </div>
        </div>
      </div>

      {/* Card sections */}
      {sections.map(([heading, cards]) => (
        <div key={heading}>
          <div className="text-[11px] uppercase tracking-wider text-[#86868B] font-bold mb-3 px-1">{heading}</div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {cards.map(c => (
              <Card
                key={c.id}
                testid={`cc-card-${c.id}`}
                icon={c.icon}
                title={c.title}
                sub={c.sub}
                value={c.value}
                accent={c.accent}
                danger={c.danger}
                onClick={c.onClick}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
